import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/utils/supabase'
import toast from 'react-hot-toast'

interface AuthState {
  user: User | null
  session: Session | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  
  // Actions
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, firstName: string, lastName: string, organizationName?: string) => Promise<void>
  logout: () => Promise<void>
  loadUser: () => Promise<void>
  clearError: () => void
  setUser: (user: User | null) => void
  setSession: (session: Session | null) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      session: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        try {
          set({ isLoading: true, error: null })
          

          
          const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password
          })
          
          if (error) {
            throw error
          }
          
          set({
            user: data.user,
            session: data.session,
            isAuthenticated: !!data.user,
            isLoading: false,
            error: null,
          })
          
          toast.success('Welcome back!')
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.message || 'Login failed',
          })
          throw error
        }
      },

      register: async (email: string, password: string, firstName: string, lastName: string, organizationName?: string) => {
        try {
          set({ isLoading: true, error: null })
          
          // Sign up with Supabase Auth
          const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
              data: {
                first_name: firstName,
                last_name: lastName,
                organization_name: organizationName
              }
            }
          })
          
          if (error) {
            throw error
          }
          
          // Create user profile in our database
          if (data.user) {
            const { error: profileError } = await supabase
              .from('users')
              .insert([{
                id: data.user.id,
                email: data.user.email,
                first_name: firstName,
                last_name: lastName,
                email_verified: false,
                status: 'ACTIVE'
              }])
            
            if (profileError) {
              console.error('Error creating user profile:', profileError)
              // Don't throw here as the auth user was created successfully
            }
            
            // Create organization if provided
            if (organizationName) {
              const slug = organizationName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
              
              const { data: organization, error: orgError } = await supabase
                .from('organizations')
                .insert([{
                  name: organizationName,
                  slug: slug,
                  description: `${organizationName} organization`
                }])
                .select()
                .single()
              
              if (!orgError && organization) {
                // Add user to organization as owner
                await supabase
                  .from('organization_users')
                  .insert([{
                    organization_id: organization.id,
                    user_id: data.user.id,
                    role: 'OWNER',
                    permissions: ['*']
                  }])
              }
            }
          }
          
          set({
            user: data.user,
            session: data.session,
            isAuthenticated: !!data.user,
            isLoading: false,
            error: null,
          })
          
          toast.success('Account created successfully! Please check your email to verify your account.')
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.message || 'Registration failed',
          })
          throw error
        }
      },

      logout: async () => {
        try {
          const { error } = await supabase.auth.signOut()
          
          if (error) {
            throw error
          }
          
          set({
            user: null,
            session: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          })
          
          toast.success('Logged out successfully')
        } catch (error: any) {
          console.error('Logout error:', error)
          // Even if logout fails, clear local state
          set({
            user: null,
            session: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          })
        }
      },

      loadUser: async () => {
        try {
          set({ isLoading: true })
          

          
          const { data: { session }, error } = await supabase.auth.getSession()
          
          if (error) {
            throw error
          }
          
          if (session) {
            set({
              user: session.user,
              session,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            })
          } else {
            set({
              user: null,
              session: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
            })
          }
        } catch (error: any) {
          console.error('Load user error:', error)
          set({
            user: null,
            session: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          })
        }
      },

      clearError: () => {
        set({ error: null })
      },

      setUser: (user: User | null) => {
        set({ user, isAuthenticated: !!user })
      },

      setSession: (session: Session | null) => {
        set({ 
          session, 
          user: session?.user || null,
          isAuthenticated: !!session?.user 
        })
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        session: state.session,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)

// Set up auth state listener
supabase.auth.onAuthStateChange((event, session) => {
  const { setSession } = useAuthStore.getState()
  setSession(session)
  
  if (event === 'SIGNED_OUT') {
    // Clear any cached data
    localStorage.removeItem('auth-storage')
  }
})

// Custom hook for easier usage
export const useAuth = () => {
  const store = useAuthStore()
  
  return {
    ...store,
    // Computed values
    userId: store.user?.id,
    email: store.user?.email,
    isEmailVerified: store.user?.email_confirmed_at !== null,
  }
}

// Helper functions
export const requireAuth = (callback: () => void) => {
  const { isAuthenticated } = useAuthStore.getState()
  if (isAuthenticated) {
    callback()
  } else {
    toast.error('Please login to continue')
    window.location.href = '/auth/login'
  }
}

export const getAccessToken = async (): Promise<string | null> => {
  const { session } = useAuthStore.getState()
  return session?.access_token || null
}